import React, { useState, useEffect } from 'react';
import { List } from 'react-native-paper';
import {
  KeyboardAvoidingView,
  Pressable,
  Keyboard,
  View,
  TouchableWithoutFeedback,
  TextInput,
  ScrollView,
  Image,
} from 'react-native';
import { KeyboardAccessoryNavigation } from 'react-native-keyboard-accessory';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import { InlineLoader } from '../components/LoadingOverlay';
import CustomText from '../components/CustomText.js';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
import MonetaImageGallery from '../components/MonetaImageGallery.js';
import { UploadProgressOverlay } from '../components/report';
import Util from '../util/util.js';
import { trackEngagementAsync } from '../util/engagementTracker';
import { useFormValidation, validateRequired } from '../util/validation';
import { useResponsiveStyles } from '../hooks';
import styles from '../style/screens/submitBugStyles';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const SubmitBug = (props) => {
  const { t } = useTranslation(['support', 'common']);

  const { organization, admin, noLogin, location, userAdmin } = props.route.params;

  // Use responsive styles hook for platform detection
  const { isWeb, isDesktop, containerStyle } = useResponsiveStyles();

  const [imagesToSend, setImagesToSend] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]); //Required for upload successful images button

  const [uploadProgress, setUploadProgress] = useState({});

  const [uploadStage, setUploadStage] = useState('Picking');

  const [imageLoading, setImageLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { values, setDisplayErrors, handleChange, validate, getFieldError } = useFormValidation(
    { description: '' },
    { description: [(v) => validateRequired(v, t('support:descriptionField'))] },
  );
  const description = values.description;

  useEffect(() => {
    Util.executeQRSignIn(organization, noLogin);
  }, []);

  //Update uploadProg dict when image is picked or dropped - this should never happen during upload time
  useEffect(() => {
    var uploadProg = {};
    imagesToSend.forEach((value, index) => {
      uploadProg[value] = 'Picked';
    });
    setUploadProgress(() => uploadProg);
  }, [imagesToSend]);

  const postBug = async () => {
    if (isSubmitting) {
      return;
    } // Don't submit duplicates!

    // Validate form before proceeding. This will prevent invalid reports from being submitted.
    setDisplayErrors(true);
    if (!validate().isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      let [fullImageURLs, _] = await Util.getUploadedImageURLs(
        true,
        uploadedImages,
        imagesToSend,
        setUploadedImages,
        setUploadStage,
        setUploadProgress,
      );

      let userEmail = await Util.getUserEmail();
      // Build email, and send it with bug report info
      let details =
        '<b>Sent:</b> ' +
        // Pinned to English: this is an email body whose surrounding labels are
        // English, and the recipient is not the submitter. Per-recipient
        // localization is a backend concern.
        Util.timeConverter(Date.now(), { locale: 'en' }) +
        '<br/>' +
        '<b>User:</b> ' +
        userEmail +
        '<br />' +
        '<b>Organization:</b> ' +
        organization +
        '<br /><br />' +
        '<b>Description</b>: ' +
        description +
        '<br />';

      if (fullImageURLs.length > 0) {
        details += '<br/><br/><b>Screenshots:</b><br/>';
        for (const img in fullImageURLs) {
          details += `<img src="cid:unique@screenshot-${img}" style="width: 100px"/><br/>`;
        }
      }

      let message = 'A bug report was submitted.<br/><br/>';

      Util.sendEmailNotification(
        details,
        message,
        'bugSubmitted',
        organization,
        userEmail,
        fullImageURLs,
      );

      Toast.show(`✅ ${t('support:submitted')}`, {
        backgroundColor: '#259e23',
        position: Toast.positions.BOTTOM,
        duration: 2500,
        textColor: '#fff',
      });

      // Track bug submission for surprise badges (shows toast if badge earned)
      trackEngagementAsync('bug_submitted');

      setIsSubmitting(false);
      props.navigation.pop();
    } catch (e) {
      alert(`❌ ${t('support:submitFailed')}`, e.message);
      setIsSubmitting(false);
    }
  };

  const selectPhotoOptions = async () => {
    // No need to offer options for submitting screenshots. They will always
    // be in the gallery.
    Util.pickImage(Util.imageMode.gallery, setImageLoading, setImagesToSend);
  };

  return (
    <View
      style={[
        containerStyle,
        styles.container,
      ]}
    >
      <Header organization={organization} admin={admin} noLogin={noLogin} location={location} userAdmin={userAdmin} />
      <UploadProgressOverlay
        uploadStage={uploadStage}
        uploadProgress={uploadProgress}
        isDesktop={isDesktop}
        showFailedActions={uploadStage === 'Failed' || Util.anyUploadsFailed(uploadProgress)}
        onCancel={() => setUploadStage('Picking')}
        onRetry={() => postBug()}
        onSubmitWithSuccessful={() => postBug()}
      />

      <ScrollView style={styles.innerContainer}>
        <TouchableWithoutFeedback
          onPress={!isWeb ? Keyboard.dismiss : () => {}}
          accessible={false}
        >
          <View style={{ flex: 1 }}>
            {/* V3: Page title */}
            <CustomText
              style={styles.title}
              font="semiBold"
              size={isWeb ? 28 : 24}
              color={AppStyles.colorSet.textPrimary}
            >
              {t('support:title')}
            </CustomText>

            {/* V3: Contact info */}
            <View style={styles.sectionContainer}>
              <View style={styles.contactCard}>
                <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                  {t('support:contactPrompt')}
                </CustomText>
                <CustomText font="medium" size={16} color={AppStyles.colorSet.textPrimary}>
                  support@monetatech.net
                </CustomText>
              </View>
            </View>

            {/* V3: Bug report section */}
            <View style={styles.sectionContainer}>
              <CustomText
                style={styles.sectionLabel}
                font="medium"
                size={14}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('support:reportIssue')}
              </CustomText>

              {/* Description textbox */}
              <KeyboardAvoidingView style={styles.orangeContainer}>
                {description === '' && (
                  <CustomText style={styles.placeholder}>
                    {t('support:descriptionPlaceholder')}
                  </CustomText>
                )}
                <TextInput
                  allowFontScaling={false}
                  editable
                  multiline
                  numberOfLines={8}
                  onChangeText={handleChange('description')}
                  placeholder=""
                  style={styles.textInput}
                />
              </KeyboardAvoidingView>

              {getFieldError('description') && (
                <CustomText style={styles.error} font="regular" size={13}>
                  {getFieldError('description')}
                </CustomText>
              )}

              {/* V3: Add screenshot link */}
              <Pressable
                onPress={selectPhotoOptions}
                style={({ pressed }) => [styles.imageUploadLink, pressed && { opacity: 0.7 }]}
              >
                <Feather name="plus" size={18} color={AppStyles.colorSet.brandPrimary} />
                <CustomText font="medium" size={14} color={AppStyles.colorSet.brandPrimary} style={{ marginLeft: 6 }}>
                  {t('support:addScreenshot')} {imageLoading && <InlineLoader />}
                </CustomText>
              </Pressable>

              <MonetaImageGallery
                images={imagesToSend}
                setImages={setImagesToSend}
                allowDeletion={true}
              />

              {/* V3: Submit button */}
              <Pressable
                style={({ pressed }) => [
                  styles.submitButton,
                  !description.length && styles.submitButtonDisabled,
                  pressed && description.length && { opacity: 0.8 },
                ]}
                onPress={() => postBug()}
                disabled={!description.length}
              >
                <CustomText
                  font="medium"
                  size={16}
                  color={!description.length ? AppStyles.colorSet.textMuted : '#000'}
                >
                  {t('support:submitBug')} {isSubmitting && <InlineLoader />}
                </CustomText>
              </Pressable>
            </View>

            {/* V3: Separator */}
            <View style={[styles.separator, styles.sectionContainer]} />

            {/* V3: FAQ section */}
            <View style={styles.faqContainer}>
              <CustomText
                style={styles.faqHeading}
                font="semiBold"
                size={20}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('support:faqTitle')}
              </CustomText>
              <List.Section>
                <List.Accordion title={t('support:faq.voiceToTextTitle')}>
                  <List.Section>
                    <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={styles.accordionText}>
                      {t('support:faq.voiceToTextBody')}
                    </CustomText>
                    <Image
                      source={require('../assets/images/voice-to-text.jpg')}
                      style={styles.faqSingleScreenshot}
                    />
                  </List.Section>
                </List.Accordion>
                <List.Accordion title={t('support:faq.uploadPictureTitle')}>
                  <List.Section>
                    <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={styles.accordionText}>
                      {t('support:faq.uploadPictureBody')}
                    </CustomText>
                    <Image
                      source={require('../assets/images/camera.jpg')}
                      style={styles.faqSingleScreenshot}
                    />
                  </List.Section>
                </List.Accordion>
                <List.Accordion title={t('support:faq.cameraTitle')}>
                  <List.Section>
                    <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={styles.accordionText}>
                      {t('support:faq.cameraBody')}
                    </CustomText>
                    <View style={styles.faqMultiScreenshotContainer}>
                      <Image
                        source={require('../assets/images/apps_notifications.jpg')}
                        style={styles.faqMultiScreenshot}
                      />
                      <Image
                        source={require('../assets/images/moneta_android.jpg')}
                        style={styles.faqMultiScreenshot}
                      />
                    </View>
                    <View style={styles.faqMultiScreenshotContainer}>
                      <Image
                        source={require('../assets/images/permissions.jpg')}
                        style={styles.faqMultiScreenshot}
                      />
                      <Image
                        source={require('../assets/images/camera_permissions.jpg')}
                        style={styles.faqMultiScreenshot}
                      />
                    </View>
                    <View style={styles.faqMultiScreenshotContainer}>
                      <Image
                        source={require('../assets/images/allow_camera.jpg')}
                        style={styles.faqMultiScreenshot}
                      />
                    </View>
                  </List.Section>
                </List.Accordion>
              </List.Section>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </ScrollView>
      <Footer admin={admin} organization={organization} noLogin={noLogin} location={location} userAdmin={userAdmin} />
      {!isWeb && <KeyboardAccessoryNavigation androidAdjustResize avoidKeyboard />}
    </View>
  );
};

export default SubmitBug;
